import type { ReactNode } from 'react'
import { Search, X } from 'lucide-react'
import { type Table } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group'
import { cn } from '@/shared/lib/utils'
import { DataTableFacetedFilter, type FacetedFilterOption } from './faceted-filter'
import { DataTableViewOptions } from './view-options'

type DataTableToolbarProps<TData> = {
  table: Table<TData>
  searchPlaceholder?: string
  searchKey?: string
  searchClassName?: string
  filters?: {
    columnId: string
    title: string
    showCounts?: boolean
    options: FacetedFilterOption[]
  }[]
  /** When false, hides option counts on all toolbar faceted filters. Default true. */
  showFilterCounts?: boolean
  activeFilterBadges?: ReactNode
  filterActions?: ReactNode
  showViewOptions?: boolean
  actions?: ReactNode
}

export function DataTableToolbar<TData>({
  table,
  searchPlaceholder,
  searchKey,
  searchClassName,
  filters = [],
  showFilterCounts = true,
  activeFilterBadges,
  filterActions,
  showViewOptions = true,
  actions,
}: DataTableToolbarProps<TData>) {
  const { t } = useTranslation('common')
  const isFiltered =
    (table.getState().columnFilters?.length ?? 0) > 0 ||
    table.getState().globalFilter

  const placeholder = searchPlaceholder ?? t('dataTable.noResults')
  const searchInputClassName = cn(
    'h-8 w-full max-w-xl shrink-0',
    searchClassName
  )
  const showToolbarActions = actions != null || showViewOptions

  return (
    <div className='flex flex-col gap-y-2'>
      <div className='flex items-center justify-between gap-x-2'>
        <div className='flex min-w-0 flex-1 items-center gap-x-2'>
          {searchKey ? (
            <InputGroup className={searchInputClassName}>
              <InputGroupAddon align='inline-start'>
                <Search aria-hidden />
              </InputGroupAddon>
              <InputGroupInput
                placeholder={placeholder}
                value={
                  (table.getColumn(searchKey)?.getFilterValue() as string) ??
                  ''
                }
                onChange={(event) =>
                  table.getColumn(searchKey)?.setFilterValue(event.target.value)
                }
              />
            </InputGroup>
          ) : (
            <InputGroup className={searchInputClassName}>
              <InputGroupAddon align='inline-start'>
                <Search aria-hidden />
              </InputGroupAddon>
              <InputGroupInput
                placeholder={placeholder}
                value={table.getState().globalFilter ?? ''}
                onChange={(event) => table.setGlobalFilter(event.target.value)}
              />
            </InputGroup>
          )}
          {isFiltered && (
            <Button
              variant='ghost'
              onClick={() => {
                table.resetColumnFilters()
                table.setGlobalFilter('')
              }}
              className='h-8 shrink-0 px-2 lg:px-3'
            >
              {t('dataTable.resetFilters')}
              <X className='ms-2 h-4 w-4' />
            </Button>
          )}
        </div>
        {showToolbarActions && (
          <div className='flex shrink-0 items-center gap-x-2'>
            {actions}
            {showViewOptions && <DataTableViewOptions table={table} />}
          </div>
        )}
      </div>
      {(filters.length > 0 || filterActions != null) && (
        <div className='flex flex-wrap items-center gap-2'>
          {filters.map((filter) => {
            const column = table.getColumn(filter.columnId)
            if (!column) {
              return null
            }
            return (
              <DataTableFacetedFilter
                key={filter.columnId}
                column={column}
                title={filter.title}
                showCounts={filter.showCounts ?? showFilterCounts}
                options={filter.options}
              />
            )
          })}
          {filterActions}
        </div>
      )}
      {activeFilterBadges}
    </div>
  )
}
